/* eslint-disable max-lines */
import {
  BedDouble, ChartColumn, Coffee, DoorOpen, LayoutDashboard, Puzzle, Users,
} from 'lucide-react';
import React from 'react';

import {
  ActivityJournalIcon,
  AiAgentIcon,
  AllEventsIcon,
  AmenitiesIcon,
  AnalyticsIcon,
  BellIcon,
  BreakfastIcon,
  BusinessCardIcon,
  CommerceIcon,
  CompassIcon,
  ConferencesIcon,
  DashboardIcon,
  EventsIcon,
  FaqIcon,
  GoodPracticeIcon,
  HotelIcon,
  InfoIcon,
  ListChecksIcon,
  LoyaltyStarIcon,
  MarketingIcon,
  MediaLibraryIcon,
  OffersIcon,
  OperaCloudIcon,
  PhonebookIcon,
  PlusIcon,
  PoiIcon,
  ProductsBagIcon,
  PromoIcon,
  PushClickIcon,
  QrIcon,
  RegulationsIcon,
  RentalCartIcon,
  ReportsIcon,
  ReservationsIcon,
  ReturnsIcon,
  ReviewsIcon,
  RoomServiceIcon,
  SalesAnalyticsIcon,
  ServicesIcon,
  SettingsIcon,
  ShapesIcon,
  SocialMediaIcon,
  SpaFlowerIcon,
  StoreBoxIcon,
  StripeTileIcon,
  SubscribersIcon,
  SurveysIcon,
  TagIcon,
  TvIcon,
  WidgetsIcon,
  WifiIcon,
} from '../components/SidebarIcons';
import type { MenuItemProps, SubitemProps } from '../types';

// Menu w czterech sekcjach (`kind: 'divider'`): Sprzedaż → Hotel i oferta →
// Marketing i analityka → System. Kolejność idzie od tego, co recepcja otwiera
// codziennie, do konfiguracji odwiedzanej raz na jakiś czas. Wpisy `group` mają
// podstrony rozwijane w flyout-popoverze po najechaniu na rodzica.
const getBaseMenuConfig = (): MenuItemProps[] => [
  {
    kind: 'item',
    pathName: 'Dashboard',
    labelKey: 'menu.dashboard',
    path: '/',
    icon: <DashboardIcon size={18} />,
  },

  /**
   * Sekcja SPRZEDAŻ — pieniądze i praca, którą one generują, w jednym miejscu.
   *
   * Stoi zaraz pod Dashboardem, bo recepcja zagląda tu najczęściej. Sekcja Opery jest
   * wstrzykiwana na indeks 1 (`ProtectedRoute`), więc PMS ląduje nad tym nagłówkiem.
   */
  { kind: 'divider', dividerLabel: 'Sprzedaż' },

  {
    kind: 'group',
    pathName: 'Płatności',
    labelKey: 'menu.payments',
    path: '/payments',
    // Znak Stripe w fioletowym kwadracie, jak jego oficjalne logo.
    icon: <StripeTileIcon size={18} />,
    subItem: [
      {
        pathName: 'Transakcje',
        labelKey: 'menu.transactions',
        path: '/payments',
        icon: <SalesAnalyticsIcon size={18} />,
      },
      /* Zwroty osobno, bo to inna praca niż przeglądanie wpływów: lista zadań
         „oddaj pieniądze gościowi", zebrana ze wszystkich punktów naraz. */
      {
        pathName: 'Zwroty środków',
        labelKey: 'menu.refunds',
        path: '/payments/refunds',
        icon: <ReturnsIcon size={18} />,
      },
    ],
  },

  {
    kind: 'group',
    pathName: 'G-Commerce',
    labelKey: 'menu.gCommerce',
    path: '/orders',
    icon: <CommerceIcon size={18} />,
    subItem: [
      {
        pathName: 'Zamówienia Room Service',
        labelKey: 'menu.roomServiceOrders',
        path: '/orders',
        icon: <RoomServiceIcon size={18} />,
      },
      {
        pathName: 'Zakupy Store',
        labelKey: 'menu.storePurchases',
        path: '/order-products',
        icon: <StoreBoxIcon size={18} />,
      },
    ],
  },

  // Rezerwacje zebrane per outlet w jednym miejscu: wellness + wypożyczalnia.
  {
    kind: 'group',
    pathName: 'Rezerwacje',
    labelKey: 'menu.reservations',
    path: '/reservations',
    icon: <ReservationsIcon size={18} />,
    subItem: [
      {
        pathName: 'Wellness & Lifestyle',
        labelKey: 'menu.wellness',
        path: '/reservations/wellness',
        icon: <SpaFlowerIcon size={18} />,
      },
      {
        pathName: 'Wypożyczalnia Produktów',
        labelKey: 'menu.productRental',
        path: '/product-rental-reservations',
        icon: <RentalCartIcon size={18} />,
      },
    ],
  },

  {
    kind: 'item',
    pathName: 'Zgłoszenia',
    labelKey: 'menu.reports',
    path: '/reports',
    icon: <ReportsIcon size={18} />,
  },

  // Co gość widzi i co może u nas kupić — treść hotelu razem z katalogiem oferty.
  { kind: 'divider', dividerLabel: 'Hotel i oferta' },

  {
    kind: 'group',
    pathName: 'Mój Hotel',
    labelKey: 'menu.myHotel',
    path: '/hotel',
    icon: <HotelIcon size={18} />,
    subItem: [
      {
        pathName: 'Informacje hotelowe',
        labelKey: 'menu.hotelInformation',
        path: '/hotel/general-information',
        icon: <InfoIcon size={18} />,
      },
      {
        pathName: 'Udogodnienia',
        labelKey: 'menu.amenities',
        path: '/hotel/amenities',
        icon: <AmenitiesIcon size={18} />,
      },
      {
        pathName: 'Wizytówki',
        labelKey: 'menu.businessCards',
        path: '/hotel/business-card',
        icon: <BusinessCardIcon size={18} />,
      },
      {
        pathName: 'Dobre praktyki',
        labelKey: 'menu.goodPractices',
        path: '/hotel/good-practice',
        icon: <GoodPracticeIcon size={18} />,
      },
      {
        pathName: 'Książka telefoniczna',
        labelKey: 'menu.phoneBook',
        path: '/hotel/phonebook',
        icon: <PhonebookIcon size={18} />,
      },
      {
        pathName: 'Wifi',
        labelKey: 'menu.wifi',
        path: '/hotel/wifi',
        icon: <WifiIcon size={18} />,
      },
      {
        pathName: 'Regulaminy',
        labelKey: 'menu.regulations',
        path: '/hotel/regulations',
        icon: <RegulationsIcon size={18} />,
      },
      {
        pathName: 'Poi',
        labelKey: 'menu.poi',
        path: '/hotel/poi',
        icon: <PoiIcon size={18} />,
      },
      {
        pathName: 'Kanały TV',
        labelKey: 'menu.tvChannels',
        path: '/hotel/tv-chanel',
        icon: <TvIcon size={18} />,
      },
      {
        pathName: 'Faq',
        labelKey: 'menu.faq',
        path: '/hotel/faq',
        icon: <FaqIcon size={18} />,
      },
      {
        pathName: 'Program Lojalnościowy',
        labelKey: 'menu.loyaltyProgram',
        path: '/hotel/loyalty-program',
        icon: <LoyaltyStarIcon size={18} />,
      },
      {
        pathName: 'SocialMedia',
        labelKey: 'menu.socialMedia',
        path: '/hotel/social-media',
        icon: <SocialMediaIcon size={18} />,
      },
      {
        pathName: 'Wirtualny spacer',
        labelKey: 'menu.virtualTour',
        path: '/hotel/virtual-tour',
        icon: <CompassIcon size={18} />,
      },
      {
        pathName: 'Linki do recenzji',
        labelKey: 'menu.reviewLinks',
        path: '/hotel/link-to-reviews',
        icon: <ReviewsIcon size={18} />,
      },
      {
        pathName: 'Bloki niestandardowe',
        labelKey: 'menu.customBlocks',
        path: '/hotel/custom-blocks',
        icon: <ShapesIcon size={18} />,
      },
    ],
  },

  {
    kind: 'item',
    pathName: 'Generator QR',
    labelKey: 'menu.qrGenerator',
    path: '/qr-generator',
    icon: <QrIcon size={18} />,
  },

  {
    kind: 'group',
    pathName: 'Usługi',
    labelKey: 'menu.services',
    path: '/spa',
    icon: <ServicesIcon size={18} />,
    subItem: [
      {
        pathName: 'Spa',
        labelKey: 'menu.spa',
        path: '/spa',
        icon: <SpaFlowerIcon size={18} />,
      },
      {
        pathName: 'Smart Room Service',
        labelKey: 'menu.roomService',
        path: '/take-to-room-service',
        icon: <RoomServiceIcon size={18} />,
      },
      {
        pathName: 'Serwis śniadaniowy',
        labelKey: 'menu.breakfastService',
        path: '/breakfast-service',
        icon: <BreakfastIcon size={18} />,
      },
      {
        pathName: 'Produkty i Usługi',
        labelKey: 'menu.productsServices',
        path: '/products-services',
        icon: <ProductsBagIcon size={18} />,
      },
      {
        pathName: 'Wypożyczalnia Produktów',
        labelKey: 'menu.productRental',
        path: '/product-rental',
        icon: <RentalCartIcon size={18} />,
      },
    ],
  },

  {
    kind: 'group',
    pathName: 'Oferty Specjalne',
    labelKey: 'menu.specialOffers',
    path: '/special-offer',
    icon: <OffersIcon size={18} />,
    subItem: [
      {
        pathName: 'Wszystkie',
        labelKey: 'menu.all',
        path: '/special-offer',
        icon: <ListChecksIcon size={18} />,
      },
      {
        pathName: 'Utwórz ofertę',
        labelKey: 'menu.addOffer',
        path: '/special-offer/new',
        icon: <PlusIcon size={18} />,
      },
      {
        pathName: 'Kategorie',
        labelKey: 'menu.categories',
        path: '/special-offer/categories',
        icon: <TagIcon size={18} />,
      },
    ],
  },

  {
    kind: 'group',
    pathName: 'Wydarzenia',
    labelKey: 'menu.events',
    path: '/events',
    icon: <EventsIcon size={18} />,
    subItem: [
      {
        pathName: 'Wszystkie',
        labelKey: 'menu.all',
        path: '/events/all-events',
        icon: <AllEventsIcon size={18} />,
      },
      {
        pathName: 'Utwórz wydarzenie',
        labelKey: 'menu.addEvent',
        path: '/events/new',
        icon: <PlusIcon size={18} />,
      },
      {
        pathName: 'Kategorie',
        labelKey: 'menu.categories',
        path: '/events/categories',
        icon: <TagIcon size={18} />,
      },
      {
        pathName: 'Lista rezerwacji',
        labelKey: 'menu.reservationList',
        path: '/events/register',
        icon: <ListChecksIcon size={18} />,
      },
    ],
  },

  {
    kind: 'item',
    pathName: 'Konferencje',
    labelKey: 'menu.conferences',
    path: '/conferences',
    icon: <ConferencesIcon size={18} />,
  },

  // Dotarcie do gościa i sprawdzenie, czy zadziałało — promocja obok liczb.
  { kind: 'divider', dividerLabel: 'Marketing i analityka' },

  {
    kind: 'group',
    pathName: 'Marketing',
    labelKey: 'menu.marketing',
    path: '/alerts',
    icon: <MarketingIcon size={18} />,
    subItem: [
      {
        pathName: 'Powiadomienia',
        labelKey: 'menu.notifications',
        path: '/alerts',
        icon: <BellIcon size={18} />,
      },
      {
        pathName: 'Kody rabatowe',
        labelKey: 'menu.promoCodes',
        path: '/promo-codes',
        icon: <PromoIcon size={18} />,
      },
      {
        pathName: 'Kampanie Push',
        labelKey: 'menu.pushCampaigns',
        path: '/marketing',
        icon: <PushClickIcon size={18} />,
      },
      {
        pathName: 'Subskrybenci',
        labelKey: 'menu.subscribers',
        path: '/subscribers',
        icon: <SubscribersIcon size={18} />,
      },
    ],
  },

  {
    kind: 'item',
    pathName: 'Widgety',
    labelKey: 'menu.widgets',
    path: '/widgets',
    icon: <WidgetsIcon size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Ankiety',
    labelKey: 'menu.surveys',
    path: '/surveys',
    icon: <SurveysIcon size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Analityka',
    labelKey: 'menu.analytics',
    path: '/analytics',
    icon: <AnalyticsIcon size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Analiza Sprzedaży',
    labelKey: 'menu.salesAnalytics',
    path: '/sales-analytics',
    icon: <SalesAnalyticsIcon size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Dziennik aktywności',
    labelKey: 'menu.activityJournal',
    path: '/activity-journal',
    icon: <ActivityJournalIcon size={18} />,
  },

  // Konfiguracja, do której wchodzi się raz na jakiś czas.
  { kind: 'divider', dividerLabel: 'System' },

  {
    kind: 'item',
    pathName: 'AI Agents',
    labelKey: 'menu.aiAgents',
    path: '/ai-agents',
    icon: <AiAgentIcon size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Biblioteka mediów',
    labelKey: 'menu.mediaLibrary',
    path: '/media-library',
    icon: <MediaLibraryIcon size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Ustawienia',
    labelKey: 'menu.settings',
    path: '/settings',
    icon: <SettingsIcon size={18} />,
  },
];

const getDividerLabel = (label: string | undefined, t: (key: string) => string): string => {
  if (!label) return '';
  const labelMap: Record<string, string> = {
    Zarządzanie: t('sidebar.management'),
    Hotel: t('sidebar.hotel'),
    Sprzedaż: t('sidebar.sales'),
    'Hotel i oferta': t('sidebar.hotelAndOffer'),
    'Marketing i analityka': t('sidebar.marketingAndAnalytics'),
    System: t('sidebar.system'),
    Marketing: t('sidebar.marketing'),
    'Wydarzenia i Rezerwacje': t('sidebar.eventsAndReservations'),
    Usługi: t('sidebar.services'),
    'Analityka i Feedback': t('sidebar.analyticsAndFeedback'),
    Pozostałe: t('sidebar.other'),
    Muzeum: t('sidebar.museum'),
    'Aplikacje zewnętrzne': t('sidebar.externalApplications'),
  };
  return labelMap[label] || label;
};

export const getTranslatedMenuConfig = (t: (key: string) => string): MenuItemProps[] => {
  const baseConfig = getBaseMenuConfig();
  return baseConfig.map((item) => {
    if (item.kind === 'divider') {
      return {
        ...item,
        dividerLabel: getDividerLabel(item.dividerLabel, t),
      };
    }
    if (item.kind === 'item') {
      const translations: Record<string, string> = {
        Dashboard: t('sidebar.dashboard'),
        Zgłoszenia: t('sidebar.reports'),
        Zamówienia: t('sidebar.orders'),
        Zakupy: t('sidebar.shopping'),
        Rezerwacje: t('sidebar.reservations'),
        'Oferty Specjalne': t('sidebar.specialOffers'),
        Powiadomienia: t('sidebar.notifications'),
        'Kody rabatowe': t('sidebar.promoCodes'),
        Konferencje: t('sidebar.conferences'),
        Spa: t('sidebar.spa'),
        Ankiety: t('sidebar.surveys'),
        Analityka: t('sidebar.analytics'),
        'Analiza Sprzedaży': t('sidebar.salesAnalytics'),
        Płatności: t('sidebar.payments'),
        'Dziennik aktywności': t('sidebar.activityJournal'),
        'Biblioteka mediów': t('sidebar.mediaLibrary'),
        Ustawienia: t('sidebar.settings'),
        'Kampanie Push': t('sidebar.clickAndPush'),
        Subskrybenci: t('sidebar.subscribers'),
        'Smart Room Service': t('sidebar.smartRoomService'),
        'Produkty i Usługi': t('sidebar.productsServices'),
        'Wypożyczalnia Produktów': t('sidebar.productRental'),
        'AI Agent Dashboard': t('sidebar.aiAgentDashboard'),
        'AI Agents': t('sidebar.aiAgents'),
        Widgety: t('sidebar.widgets'),
        'Generator QR': t('sidebar.qrGenerator'),
        'Moje urządzenia': t('sidebar.myDevices'),
        Museum: t('sidebar.museum'),
      };
      return {
        ...item,
        pathName: translations[item.pathName] || item.pathName,
      };
    }
    if (item.kind === 'group') {
      const groupTranslations: Record<string, string> = {
        Zarządzanie: t('sidebar.management'),
        'Mój Hotel': t('sidebar.myHotel'),
        Wydarzenia: t('sidebar.events'),
        'Oferty Specjalne': t('sidebar.specialOffers'),
        Marketing: t('sidebar.marketing'),
        Usługi: t('sidebar.services'),
        Wypożyczalnia: t('sidebar.rental'),
        'G-Commerce': t('sidebar.orders'),
        Płatności: t('sidebar.payments'),
        Rezerwacje: t('sidebar.reservations'),
      };
      const subItemTranslations: Record<string, string> = {
        // Marketing — te cztery są subitemami grupy, więc mapa dla `kind: 'item'`
        // ich nie obejmowała i zostawały po polsku niezależnie od języka.
        Powiadomienia: t('sidebar.notifications'),
        'Kody rabatowe': t('sidebar.promoCodes'),
        'Kampanie Push': t('sidebar.clickAndPush'),
        Subskrybenci: t('sidebar.subscribers'),
        'Informacje hotelowe': t('sidebar.hotelInformation'),
        Udogodnienia: t('sidebar.amenities'),
        Wizytówki: t('sidebar.businessCards'),
        'Dobre praktyki': t('sidebar.goodPractices'),
        'Książka telefoniczna': t('sidebar.phoneBook'),
        Wifi: t('sidebar.wifi'),
        Regulaminy: t('sidebar.regulations'),
        Poi: t('sidebar.poi'),
        'Kanały TV': t('sidebar.tvChannels'),
        Faq: t('sidebar.faq'),
        'Program Lojalnościowy': t('sidebar.loyaltyProgram'),
        SocialMedia: t('sidebar.socialMedia'),
        'Wirtualny spacer': t('sidebar.virtualTour'),
        'Linki do recenzji': t('sidebar.reviewLinks'),
        'Bloki niestandardowe': t('sidebar.customBlocks'),
        'Utwórz wydarzenie': t('sidebar.createEvent'),
        Wszystkie: t('sidebar.all'),
        Kategorie: t('sidebar.categories'),
        'Lista ofert': t('sidebar.offerList'),
        'Utwórz ofertę': t('sidebar.addOffer'),
        'Dodaj kategorię': t('sidebar.addCategory'),
        'Lista rezerwacji': t('sidebar.reservationList'),
        Produkty: t('sidebar.products'),
        Rezerwacje: t('sidebar.rental'),
        'Zamówienia Room Service': t('sidebar.ordersRoomService'),
        'Zakupy Store': t('sidebar.shoppingStore'),
        'Lista produktów': t('sidebar.productList'),
        // Outlety — subitemy grupy „Usługi", więc mapa dla `kind: 'item'`
        // ich nie obejmowała i zostawały po polsku/EN niezależnie od języka.
        Spa: t('sidebar.spa'),
        'Smart Room Service': t('sidebar.smartRoomService'),
        'Serwis śniadaniowy': t('sidebar.breakfastService'),
        'Produkty i Usługi': t('sidebar.productsServices'),
        'Wypożyczalnia Produktów': t('sidebar.productRental'),
        Transakcje: t('sidebar.transactions'),
        'Zwroty środków': t('sidebar.refunds'),
      };
      return {
        ...item,
        pathName: groupTranslations[item.pathName] || item.pathName,
        subItem: item.subItem?.map((sub) => ({
          ...sub,
          pathName: subItemTranslations[sub.pathName] || sub.pathName,
        })),
      };
    }
    return item;
  });
};

export const MenuConfig: MenuItemProps[] = getBaseMenuConfig();

/* Lista śniadaniowa wisi w zamówieniach, nie przy konfiguracji serwisu: to
   widok operacyjny (kuchnia patrzy na nią rano), a nie ustawienia outletu.
   Wstrzykiwana warunkowo — hotel bez serwisu śniadaniowego nie ma tu czego
   otwierać (patrz `ProtectedRoute`). */
export const getBreakfastOrdersEntry = (t: (key: string) => string): SubitemProps => ({
  pathName: t('sidebar.breakfastOrders'),
  path: '/breakfast-orders',
  icon: <BreakfastIcon size={18} />,
});

export const getTranslatedMuseumEntries = (t: (key: string) => string): MenuItemProps[] => [
  { kind: 'divider', dividerLabel: t('sidebar.museum') },
  {
    kind: 'item',
    pathName: t('sidebar.museum'),
    path: '/museum',
    icon: <LayoutDashboard size={18} />,
  },
];

export const museumEntries: MenuItemProps[] = [
  { kind: 'divider', dividerLabel: 'Muzeum' },
  {
    kind: 'item',
    pathName: 'Museum',
    labelKey: 'menu.museumPage',
    path: '/museum',
    icon: <LayoutDashboard size={18} />,
  },
];

/**
 * Sekcja PMS — doklejana tylko hotelom z włączoną integracją (patrz `ProtectedRoute`).
 * Dane pochodzą na żywo z systemu hotelowego, więc trzymamy ją osobno od naszych treści.
 */
export const getTranslatedOperaEntries = (t: (key: string) => string): MenuItemProps[] => [
  {
    kind: 'group',
    pathName: t('sidebar.opera'),
    path: '/opera',
    icon: <OperaCloudIcon size={18} />,
    subItem: [
      {
        pathName: t('sidebar.operaArrivals'),
        path: '/opera/arrivals',
        icon: <DoorOpen size={18} />,
      },
      {
        pathName: t('sidebar.operaInHouseGuests'),
        path: '/opera/in-house',
        icon: <Users size={18} />,
      },
      {
        pathName: t('sidebar.operaRooms'),
        path: '/opera/rooms',
        icon: <BedDouble size={18} />,
      },
      {
        pathName: t('sidebar.operaBreakfast'),
        path: '/opera/breakfast',
        icon: <Coffee size={18} />,
      },
      {
        pathName: t('sidebar.operaBreakfastStats'),
        path: '/opera/breakfast-stats',
        icon: <ChartColumn size={18} />,
      },
    ],
  },
];

// External Applications menu entries
export const getTranslatedExternalAppsEntries = (t: (key: string) => string): MenuItemProps[] => [
  { kind: 'divider', dividerLabel: t('sidebar.externalApplications') },
  {
    kind: 'item',
    pathName: t('sidebar.museum'),
    path: '/museum',
    icon: <Puzzle size={18} />,
  },
];
